import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { reverse } from "named-urls";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { addUserSaga } from "./saga";
import * as selectors from "./selectors";
import AddUserForm from "../../../../forms/AddUserForm";
import routes from "../../../../../routes";
import schema from "../../../../schemas/addUserSchema";
import Loader from "../../../../components/Loader";

const AddUser = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: addUserSaga });
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const history = useHistory();

  useEffect(() => {
    dispatch(actions.fetchCompanyDetails({ id: companyId }));
    dispatch(actions.fetchCompanyBillingAddress({ id: companyId, query: "" }));
    dispatch(actions.fetchCompanyPickupAddress({ id: companyId, query: "" }));
  }, [dispatch, companyId]);

  const companyDetails = useSelector(selectors.selectCompanyDetails) || {};
  const loading = useSelector(selectors.selectLoading);
  const pickupAddress = useSelector(selectors.selectCompanyPickupAddreses);
  const billingAddress = useSelector(selectors.selectCompanyBillingAddreses);
  const error = useSelector(selectors.selectError);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      pickupAddress: [],
      billingAddress: [],
      company: companyId,
    },
  });

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Creating User",
        description: error,
      });
    }
    setTimeout(() => {
      dispatch(actions.initializeError());
    }, 1500);
  }, [error, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "User got added",
    });
    history.push(
      reverse(`${routes.backOffice.companies.details.users}`, { companyId })
    );
  };

  const onSubmit = (data) => {
    const payload = {
      onSuccess: handleSuccess,
      data,
    };
    dispatch(actions.addUser(payload));
  };

  const handleRoleSelect = (val) => {
    if (val === "company_admin") {
      const allPickup = pickupAddress?.map((addr) => addr._id);
      const allBilling = billingAddress?.map((addr) => addr._id);
      setValue("pickupAddress", allPickup);
      setValue("billingAddress", allBilling);
    } else {
      setValue("pickupAddress", []);
      setValue("billingAddress", []);
    }
  };

  if (
    (pickupAddress && pickupAddress.length === 0) ||
    (billingAddress && billingAddress.length === 0)
  ) {
    return (
      <div style={{ height: "100%" }}>
        <PageHeader
          ghost={false}
          title="Add User"
          subTitle={companyDetails.companyName}
          extra={[
            <Button key="1" onClick={() => history.goBack()}>
              Cancel
            </Button>,
          ]}
        />
        {loading ? <Loader /> : ""}
        <p style={{ fontSize: "16px" }}>
          Please add atleast one <strong>Pickup Address</strong> and one{" "}
          <strong>Billing Address</strong> for the company
        </p>
      </div>
    );
  } else
    return (
      <div style={{ height: "100%" }}>
        <PageHeader
          ghost={false}
          title="Add User"
          subTitle={companyDetails.companyName}
          extra={[
            <Button key="1" onClick={() => history.goBack()}>
              Cancel
            </Button>,
            <Button
              key="2"
              type="primary"
              loading={loading}
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>,
          ]}
        />
        <div
          style={{
            height: "calc(100vh - 137px)",
            overflowY: "auto",
            padding: 24,
          }}
        >
          {loading ? <Loader /> : ""}

          <AddUserForm
            pickupAddress={pickupAddress}
            billingAddress={billingAddress}
            control={control}
            errors={errors}
            handleRoleSelect={handleRoleSelect}
            companyDetails={companyDetails}
          />
        </div>
      </div>
    );
};

export default AddUser;
